import React, { lazy, Suspense, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { hot } from 'react-hot-loader/root';

import Page, { Content } from '../../library/page/page';
import CmsHtmlContent from '../../components/cms-html-content/cms-html-content';
import CmsPromotionCarousel from '../../components/cms-promotion-carousel/cms-promotion-carousel';
import CmsSections from '../../components/cms-sections/cms-sections';
import CmsArticleList from './components/cms-article-list/cms-article-list';
import CmsFeaturedArticleList from './components/cms-featured-article-list/cms-featured-article-list';
import CmsShoppingCategories from './components/cms-shopping-categories/cms-shopping-categories';
import CmsShoppingGuide from './components/cms-shopping-guide/cms-shopping-guide';
import DefaultLayout from '../../layouts/default-layout/default-layout';
import { ORGANIZATION_LD, WEBSITE_LD } from './home-page-constants';

import { GET_HOME_PAGE } from './graphql/home-page-queries';

import { useDataLayer, useFitment, useQuery } from '../../shared/hooks/hooks';

import { trackAction } from '../../shared/utils/analytics/analytics';
import { metaTags } from '../../shared/utils/page/page';
import { isArrayWithLength } from '../../shared/utils/validators/validators';

import {
  PAGE_NAME,
  PAGE_SECTION_NAME,
  PAGE_SUBCATEGORY,
  PAGE_TYPE
} from '../../shared/constants/data-layer';
import { SUGGESTED_SELLING_CONTEXTS } from '../../shared/constants/suggested-selling';

import './home-page.scss';

const SuggestedSellingPageSection = lazy(
  () =>
    import(
      '../../components/suggested-selling-page-section/suggested-selling-page-section'
    )
);

const cmsComponentMap = {
  CmsArticleListComponent: CmsArticleList,
  CmsFeaturedArticlesListComponent: CmsFeaturedArticleList,
  CmsHtmlComponent: CmsHtmlContent,
  CmsPromotionCarouselSectionComponent: CmsPromotionCarousel,
  CmsShoppingCategoriesComponent: CmsShoppingCategories,
  CmsShoppingGuideComponent: CmsShoppingGuide
};

function HomePage() {
  const { selectedFitment } = useFitment();
  const { updateDataLayer } = useDataLayer({
    shouldResetPageOnFitmentChange: true
  });
  const orgLDRef = useRef(JSON.stringify(ORGANIZATION_LD));
  const websiteLDRef = useRef(JSON.stringify(WEBSITE_LD));

  const { data, error } = useQuery(GET_HOME_PAGE, {
    variables: {
      id: 'homepage'
    }
  });

  const { cms } = data || {};

  const { page: cmsPage } = cms || {};
  const {
    contentSlots = [],
    documentTitle,
    metaTags: metaData
  } = cmsPage || {};

  const cmsPageId = 'home';

  useEffect(() => {
    if (isArrayWithLength(contentSlots)) {
      updateDataLayer({
        [PAGE_NAME]: cmsPageId,
        [PAGE_SECTION_NAME]: documentTitle,
        [PAGE_SUBCATEGORY]: cmsPageId,
        [PAGE_TYPE]: cmsPageId
      });
    }
  }, [contentSlots, documentTitle, updateDataLayer]);

  useEffect(() => {
    function cmsContentTracking(e) {
      const linkText = e?.target?.innerText?.trim();

      if (linkText === 'use our tire guide') {
        trackAction('treadwell_use_tire_guide_click');
      } else if (linkText === 'get started') {
        trackAction('treadwell_touchless_get_started_click');
      }
    }

    document.body.addEventListener('click', cmsContentTracking);

    return () => {
      document.body.removeEventListener('click', cmsContentTracking);
    };
  }, []);

  if (error && !window.isPreRendering) {
    throw error;
  }

  return (
    <DefaultLayout>
      <Page>
        {!window.isPreRendering && (
          <Helmet>
            {documentTitle && <title>{documentTitle}</title>}
            {metaTags(metaData)}
            <link
              href={`${window.location.protocol}//${window.location.host}`}
              rel="canonical"
            />
            <script type="application/ld+json">{orgLDRef.current}</script>
            <script type="application/ld+json">{websiteLDRef.current}</script>
          </Helmet>
        )}

        <Content isFullWidth>
          <CmsSections
            componentMap={cmsComponentMap}
            sections={contentSlots}
            styleName="cms-sections"
          />

          {selectedFitment && (
            <Suspense fallback={<div />}>
              <SuggestedSellingPageSection
                pageContext={SUGGESTED_SELLING_CONTEXTS.HOMEPAGE}
              />
            </Suspense>
          )}
        </Content>
      </Page>
    </DefaultLayout>
  );
}

export default hot(HomePage);
